import React, {useRef, useState} from 'react';
import Form, {FormInputComponent} from "../../components/form/Form";
import {useOpportunity} from "../../providers/OpportunityProvider";
import {Alert} from "antd";
import ReactDOM from "react-dom";
import CoverLetterTemplateModal from "./components/CoverLetterTemplateModal";
import CoverLetterChatGPTModal from './components/CoverLetterChatGPTModal';
import SpinLoader from "../../components/SpinLoader";
import Opportunity from "../../models/Opportunity";
import ProjectPosition from "../../models/ProjectPosition";

const CoverLetterEditTab = ({onUpdate = () => true, showSteps, getStepActions = false}) => {

    const {
        opportunity,
        isDisabled,
        onRequest,
        onUpdateJob,
        isUpdating
    } = useOpportunity();

    const [coverLettersResponse, setCoverLettersResponse] = useState(undefined);

    const editable = opportunity.canEditProposal();

    const formRef = useRef(null);

    const fieldProps = opportunity.isProposingAsIndividual() ? [
        'cover_letter',
        'relevant_experience'
    ] : ['cover_letter']

    const [formModel, setFormModel] = useState(TB.pick(opportunity?.job_candidate || {}, fieldProps));

    const hasAccessToEdit = opportunity.canAdmin() || opportunity.isProposingAsIndividual()

    const applyCoverLetter = async (params = {}) => {
        if (Object.keys(params).length) {
            await resetForm({...formModel, ...params});
        }
    };

    const mapping = (model) => {
        model.cover_letter = model.cover_letter || '';
        if (opportunity.isProposingAsIndividual()) {
            model.relevant_experience = model.relevant_experience || '';
        }
        return model;
    }

    const hasFormModelChanged = () => {
        let opp = mapping(TB.pick(opportunity, fieldProps));
        let model = mapping(TB.pick(formModel, fieldProps))
        return !TB.isEqual(opp, model)
    }

    const resetForm = async (params = {}) => {
        let model = TB.pick(opportunity, fieldProps)

        model = mapping({...model, ...params});

        await formRef.current.forceReset(model);
        await formRef.current.formRef.current.formsyForm.setFormPristine(true);
        return setFormModel(model)
    }

    const submitForm = async () => {
        let invalid = formRef.current.forceValidate(formModel);

        if (invalid) {
            const node = ReactDOM.findDOMNode(invalid);
            return;
        }
        let success = await onUpdate({
            job_candidate: formModel,
            talent_proposed_at: opportunity.talent_proposed_at || new Date()
        })

        if (success) {
            await resetForm();
        }
    }

    let letterRows = TB.max([
        TB.ceil((opportunity.job_candidate?.cover_letter || '')?.length / 60), 6
    ]);
    let expRows = TB.max([
        TB.ceil((opportunity.job_candidate?.relevant_experience || '')?.length / 60), 6
    ]);

    return (
        <SpinLoader isLoading={isUpdating}>
            {getStepActions && getStepActions(
                hasAccessToEdit ? 'Below please provide a cover letter highlighting your background, ' +
                    'interest in the job and relevant experience' : (opportunity.isProposingWithTeam() && 'Below is your team\'s cover letter highlighting the team\'s background, interest in the job and relevant experience. Your team admin has access to edit this information.'),
                submitForm
            )}

            <div className="opp-talent-proposal-form">
                <div className="field-section flex flex-row">
                    <div className="field basis-full lg:basis-2/3 flex flex-row items-center justify-between gap-4">
                        <div className="field-element">
                            <div className="flex flex-col">
                                <h3 className="proposal-tab-header">
                                    Cover Letter *
                                </h3>
                            </div>
                        </div>

                        <CoverLetterChatGPTModal
                            isDisabled={isDisabled}
                            onSubmit={applyCoverLetter}
                            proposalsGenerated={opportunity.project_position?.proposal_generations_count}
                            onUpdate={() => {
                                onUpdateJob(Opportunity.make({
                                    ...opportunity,
                                    project_position: ProjectPosition.make({
                                        ...opportunity.project_position,
                                        proposal_generations_count: (opportunity.project_position?.proposal_generations_count || 0) + 1
                                    })
                                }))
                            }}
                            onFetch={setCoverLettersResponse}
                        />
                    </div>


                </div>
                <Form
                    ref={formRef}
                    background={"transparent"}
                    mapping={mapping}
                    onSubmit={submitForm}
                    submitButton={false}
                    isDisabled={isDisabled}
                    model={formModel}
                    fields={[]}
                    className={
                        TB.mapClass({
                            'no-edit': !editable
                        })
                    }
                    onChange={model => setFormModel({
                        ...formModel, ...model
                    })}>


                    {opportunity.isProposingWithTeam() && !opportunity.canAdmin() && (
                        <div className="w-full max-w-[700px] ">
                            <Alert
                                type="warning"
                                showIcon
                                message={(
                                    <span><b>Note:</b>&nbsp;Only a team admin can edit/update the team's cover letter.
                                    </span>
                                )}
                            />
                        </div>
                    )}
                    <div className="field-section flex-row">
                        <div className="field basis-full lg:basis-2/3">
                            <FormInputComponent
                                key={`cover-letter-${formModel.selected_cover_letter}`}
                                fieldName={"cover_letter"}
                                value={formModel.cover_letter || ''}
                                label={'Introduction'}
                                placeholder={hasAccessToEdit ?
                                    'A good cover letter expresses your excitement over the opportunity and includes at least 3-5 bullets on why you are a good fit for the job.' : (
                                        !opportunity.isProposingAsIndividual() && 'Waiting for team admin'
                                    )}
                                type={'editor'}
                                extra={[
                                    (coverLettersResponse === undefined || coverLettersResponse?.length > 0) ?
                                        <CoverLetterTemplateModal
                                            isDisabled={isDisabled}
                                            onSubmit={applyCoverLetter}
                                            onFetch={setCoverLettersResponse}
                                        /> : undefined

                                ]}
                                disabled={!hasAccessToEdit}
                                required={true}
                                style={styles.field}
                                maxLength={5000}
                                minLength={50}
                                validation={{
                                    rules: {
                                        maxLength: 10000,
                                    },
                                    errors: {
                                        maxLength: TB.trans('validation.max.string', {
                                            'attribute': 'cover letter',
                                            'max': 10000
                                        }),
                                        minCharLength: TB.trans('validation.min.string', {
                                            'attribute': 'cover letter',
                                            'min': 50
                                        })
                                    }
                                }}
                                enableScroll={true}
                                columns={8}
                                rows={letterRows}
                            />
                        </div>
                    </div>

                    {opportunity.isProposingAsIndividual() && (
                        <div className="field-section flex-row">
                            <div className="field basis-full lg:basis-2/3">
                                <FormInputComponent
                                    key={`relevant-experience-${formModel.selected_relevant_experience}`}
                                    fieldName="relevant_experience"
                                    value={formModel.relevant_experience || ''}
                                    placeholder={hasAccessToEdit ?
                                        'Highlight your specific experience based on the client request' : (
                                            !opportunity.isProposingAsIndividual() && 'Waiting for team admin'
                                        )}
                                    type={'editor'}
                                    enableScroll={true}
                                    required={false}
                                    disabled={!hasAccessToEdit}
                                    style={styles.field}
                                    maxLength={5000}
                                    columns={8}
                                    rows={expRows}
                                />
                            </div>
                        </div>
                    )}
                    {hasAccessToEdit && editable && !showSteps && (
                        <div className="mt-3 flex flex-row justify-end">
                            <button type="submit" className="btn btn-primary mr-3"
                                    disabled={isDisabled || !hasFormModelChanged()}>
                                Save
                            </button>
                            <button className="btn btn-outline-dark"
                                    type="button" onClick={resetForm}
                                    disabled={isDisabled || !hasFormModelChanged()}>
                                Cancel
                            </button>
                        </div>
                    )}
                </Form>
            </div>
        </SpinLoader>
    );
};

const styles = {
    fieldSectionCol: {
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: 0,
    },
    rowField: {padding: '0 15px', marginBottom: 0}
};


export default CoverLetterEditTab;